// Property PMS Type Section
import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { FormSection } from '@/components/common/admin/form';
import { PMSType } from '@/types/admin/properties';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';

interface PropertyPMSTypeSectionProps {
  pmsType: PMSType;
  onPmsTypeChange: (value: PMSType) => void;
  disabled?: boolean;
}

export const PropertyPMSTypeSection: React.FC<PropertyPMSTypeSectionProps> = ({
  pmsType,
  onPmsTypeChange,
  disabled,
}) => {
  const styles = useStyles();
  const options: { value: PMSType; label: string }[] = [
    { value: 'yardi', label: 'Yardi' },
  ];

  return (
    <FormSection title="Property Management System">
      <View style={styles.container}>
        <Text style={styles.label}>PMS Type *</Text>
        <Text style={styles.helpText}>Select your property management system</Text>

        <View style={styles.optionsContainer}>
          {options.map((option) => {
            const isSelected = pmsType === option.value;
            return (
              <TouchableOpacity
                key={option.value}
                style={[
                  styles.option,
                  isSelected && styles.optionSelected,
                  disabled && styles.optionDisabled,
                ]}
                onPress={() => !disabled && onPmsTypeChange(option.value)}
                disabled={disabled}
              >
                <View style={[styles.radio, isSelected && styles.radioSelected]}>
                  {isSelected && <View style={styles.radioDot} />}
                </View>
                <Text
                  style={[
                    styles.optionLabel,
                    isSelected && styles.optionLabelSelected,
                  ]}
                >
                  {option.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    </FormSection>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors }) => {
    const accent = PRIMITIVE_COLORS.resioBlue;
    return StyleSheet.create({
      container: {
        marginBottom: 16,
      },
      label: {
        fontSize: 14,
        fontWeight: '600',
        color: colors.text.primary,
        marginBottom: 4,
      },
      helpText: {
        fontSize: 12,
        color: colors.text.dim,
        marginBottom: 12,
      },
      optionsContainer: {
        flexDirection: 'row',
        gap: 12,
      },
      option: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        padding: 12,
        borderRadius: 8,
        borderWidth: 2,
        borderColor: components.admin.formInput.border,
        backgroundColor: components.admin.formInput.bg,
      },
      optionSelected: {
        borderColor: accent,
      },
      optionDisabled: {
        opacity: 0.5,
      },
      radio: {
        width: 20,
        height: 20,
        borderRadius: 10,
        borderWidth: 2,
        borderColor: components.admin.formInput.border,
        marginRight: 8,
        alignItems: 'center',
        justifyContent: 'center',
      },
      radioSelected: {
        borderColor: accent,
      },
      radioDot: {
        width: 10,
        height: 10,
        borderRadius: 5,
        backgroundColor: accent,
      },
      optionLabel: {
        fontSize: 14,
        color: colors.text.primary,
        fontWeight: '500',
      },
      optionLabelSelected: {
        color: accent,
        fontWeight: '600',
      },
    });
  });
